import { createContext, useContext, useEffect, ReactNode } from "react";
import { useQuery, useMutation } from "convex/react";
import { useUser as useClerkUser } from "@clerk/clerk-react";
import { api } from "../../convex/_generated/api";
import { Id } from "../../convex/_generated/dataModel";
import { isAuthBypassEnabled, MOCK_USER } from "../providers/AuthBypass";

interface User {
  _id: Id<"users">;
  email: string;
  name: string;
  role: "admin" | "doctor";
  isActive: boolean;
  specialization?: string;
  licenseNumber?: string;
}

interface UserContextType {
  currentUser: User | null;
  isLoading: boolean;
  isAdmin: boolean;
  isDoctor: boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

// Bypass mode provider - returns mock user without Clerk
function BypassUserProvider({ children }: { children: ReactNode }) {
  // In bypass mode, return a mock user as admin
  const mockUser: User = {
    _id: "bypass_user_id" as Id<"users">,
    email: MOCK_USER.email,
    name: MOCK_USER.name,
    role: MOCK_USER.role,
    isActive: MOCK_USER.isActive,
  };

  return (
    <UserContext.Provider
      value={{
        currentUser: mockUser,
        isLoading: false,
        isAdmin: true,
        isDoctor: false,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

// Normal mode provider - uses Clerk
function ClerkUserProvider({ children }: { children: ReactNode }) {
  const { isSignedIn, user: clerkUser, isLoaded } = useClerkUser();

  // Get or create user in Convex when signed in
  const getOrCreateUser = useMutation(api.users.getOrCreateUser);
  const currentUser = useQuery(
    api.users.getCurrentUser,
    isSignedIn ? {} : "skip"
  );

  // Sync Clerk user with Convex on sign in
  useEffect(() => {
    const syncUser = async () => {
      if (isSignedIn && clerkUser && isLoaded) {
        try {
          await getOrCreateUser();
        } catch (error) {
          console.error("Failed to sync user with Convex:", error);
        }
      }
    };
    syncUser();
  }, [isSignedIn, clerkUser, isLoaded, getOrCreateUser]);

  const isAdmin = currentUser?.role === "admin";
  const isDoctor = currentUser?.role === "doctor";
  const isLoading = !isLoaded || (isSignedIn && currentUser === undefined);

  return (
    <UserContext.Provider
      value={{
        currentUser: currentUser as User | null,
        isLoading,
        isAdmin,
        isDoctor
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export function UserProvider({ children }: { children: ReactNode }) {
  // Use bypass provider in auth bypass mode
  if (isAuthBypassEnabled()) {
    return <BypassUserProvider>{children}</BypassUserProvider>;
  }

  return <ClerkUserProvider>{children}</ClerkUserProvider>;
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
}

// For backward compatibility
export function useDoctor() {
  const { currentUser, isDoctor } = useUser();
  return {
    currentDoctor: isDoctor ? currentUser : null,
    isLoading: false
  };
}